import { useEffect, useState } from "react";
import { api } from "../../lib/api";

const STATE_COLOR = {
  OBB: "bg-blue-900 text-white",
  COND: "bg-amber-100 text-amber-800",
  OPT: "bg-slate-100 text-slate-700",
  NA: "bg-slate-50 text-slate-400",
  DERIV: "bg-emerald-100 text-emerald-800",
};

export default function AdminMatrice() {
  const [matrice, setMatrice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.matrice().then((d) => {
      setMatrice(d);
      setLoading(false);
    });
  }, []);

  if (loading || !matrice) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-8 text-slate-500">
        Caricamento matrice...
      </div>
    );
  }

  const families = Object.keys(matrice.T1);
  // Collect all parameter codes used
  const params = Array.from(
    new Set(families.flatMap((f) => Object.keys(matrice.T1[f])))
  );
  // Sort by numeric prefix
  params.sort((a, b) => {
    const na = parseInt(a.split("_")[0].slice(1), 10);
    const nb = parseInt(b.split("_")[0].slice(1), 10);
    return na - nb;
  });

  return (
    <div className="space-y-6" data-testid="admin-matrice">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
          Matrice T1
        </h1>
        <p className="text-sm text-slate-500">
          Obbligatorietà parametri per famiglia (read-only).
        </p>
      </header>

      <div className="flex items-center gap-3 text-xs">
        {Object.entries(STATE_COLOR).map(([k, c]) => (
          <span
            key={k}
            className={`inline-flex items-center px-2 py-1 rounded-sm font-mono ${c}`}
          >
            {k}
          </span>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 border-b border-slate-200 sticky left-0 bg-slate-50">
                Famiglia
              </th>
              {params.map((p) => (
                <th
                  key={p}
                  className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 border-b border-slate-200 whitespace-nowrap"
                >
                  {p}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {families.map((f) => (
              <tr key={f} className="border-t border-slate-100">
                <td className="p-3 font-mono text-xs sticky left-0 bg-white">
                  <div className="font-semibold text-slate-900">{f}</div>
                  <div className="text-slate-500">{matrice.famiglie[f]?.nome}</div>
                </td>
                {params.map((p) => {
                  const state = matrice.T1[f][p];
                  if (!state) return <td key={p} className="p-3"></td>;
                  return (
                    <td key={p} className="p-3">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-sm font-mono text-[10px] ${
                          STATE_COLOR[state] || ""
                        }`}
                      >
                        {state}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-3">
            Settori abilitati
          </h2>
          <ul className="space-y-3">
            {Object.entries(matrice.settori).map(([code, s]) => (
              <li key={code} className="border-l-2 border-blue-900 pl-3">
                <div className="font-mono text-xs text-slate-500">{code}</div>
                <div className="font-medium text-slate-900">{s.nome}</div>
                {s.certificazioni_forzate && (
                  <div className="text-xs text-slate-600 mt-1">
                    Certificazioni:{" "}
                    <span className="font-mono">
                      {s.certificazioni_forzate.join(", ")}
                    </span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-3">
            Famiglie attive
          </h2>
          <ul className="space-y-2">
            {Object.entries(matrice.famiglie).map(([code, f]) => (
              <li
                key={code}
                className="flex items-center gap-3 border-b border-slate-100 pb-2 last:border-b-0"
              >
                <span className="font-mono text-xs px-2 py-1 bg-slate-100 rounded-sm">
                  {code}
                </span>
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900">
                    {f.nome}
                  </div>
                  <div className="text-xs text-slate-500">
                    alias: {f.alias.join(", ")}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
